import { forwardRef, useImperativeHandle, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Tablet, Trash2, Loader2, PenLine } from "lucide-react";
import { Button } from "./ui/button";
import { useWacomStuTablet } from "../hooks/useWacomStuTablet";

/**
 * Campo firma con canvas touch/mouse e supporto opzionale tavoletta Wacom STU (WebHID).
 */
const SignaturePadField = forwardRef(function SignaturePadField(
  {
    backgroundColor = "#ffffff",
    canvasClassName = "w-full touch-none",
    canvasStyle,
    containerClassName = "keep-light relative border-2 border-dashed border-slate-300 rounded-xl bg-white overflow-hidden",
    watermark = "Firma qui con il dito o il pennino",
    showWatermark = true,
    decoration = null,
    clearVariant = "button",
    clearClassName,
    actionsClassName = "flex flex-wrap items-center justify-between gap-2 mt-2",
    onSignatureChange,
  },
  ref
) {
  const canvasRef = useRef(null);
  const {
    webHidSupported,
    tabletActive,
    connecting,
    deviceLabel,
    startTablet,
    stopTablet,
    clearSignature,
    getHasTabletInk,
  } = useWacomStuTablet(canvasRef);

  const handleClearSignature = async () => {
    await clearSignature();
    onSignatureChange?.();
  };

  useImperativeHandle(ref, () => ({
    clear: () => handleClearSignature(),
    isEmpty: () => {
      // Il tratto della tavoletta viene disegnato direttamente sul canvas senza
      // passare per lo stato interno di react-signature-canvas, quindi isEmpty()
      // della libreria restituirebbe sempre true. Usiamo il flag locale.
      if (getHasTabletInk()) return false;
      return canvasRef.current?.isEmpty() ?? true;
    },
    toDataURL: (...args) => {
      // Il canvas è già aggiornato (disegno diretto), non serve sincronizzare.
      return canvasRef.current?.getCanvas()?.toDataURL(...args)
        ?? canvasRef.current?.toDataURL(...args);
    },
    fromDataURL: (...args) => canvasRef.current?.fromDataURL(...args),
    getCanvas: () => canvasRef.current?.getCanvas(),
    getSignaturePad: () => canvasRef.current?.getSignaturePad(),
    getTrimmedCanvas: () => canvasRef.current?.getTrimmedCanvas(),
  }));

  const handleTabletToggle = async () => {
    if (tabletActive) {
      await stopTablet();
    } else {
      await startTablet();
    }
  };

  return (
    <div>
      <div className={containerClassName}>
        {decoration}
        <SignatureCanvas
          ref={canvasRef}
          onEnd={() => onSignatureChange?.()}
          canvasProps={{
            className: canvasClassName,
            style: canvasStyle,
          }}
          backgroundColor={backgroundColor}
        />
        {showWatermark && !tabletActive && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="text-slate-300 text-sm select-none">{watermark}</span>
          </div>
        )}
        {tabletActive && (
          <div className="pointer-events-none absolute inset-x-0 top-0 bg-cyan-600/90 text-white text-xs font-medium py-1.5 px-3 flex items-center gap-1.5">
            <Tablet className="w-3.5 h-3.5 shrink-0" />
            <span>
              Tavoletta attiva{deviceLabel ? ` (${deviceLabel})` : ""} — firma con il pennino
            </span>
          </div>
        )}
      </div>

      <div className={actionsClassName}>
        <div className="flex flex-wrap items-center gap-2">
          {webHidSupported && (
            <Button
              type="button"
              variant={tabletActive ? "default" : "outline"}
              size="sm"
              className={
                tabletActive
                  ? "bg-cyan-600 hover:bg-cyan-700 text-white"
                  : "text-cyan-700 border-cyan-200 hover:bg-cyan-50"
              }
              onClick={handleTabletToggle}
              disabled={connecting}
              data-testid="btn-firma-tavoletta"
            >
              {connecting ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Tablet className="w-4 h-4 mr-1.5" />
              )}
              {tabletActive ? "Disattiva tavoletta" : "Firma con tavoletta grafica"}
            </Button>
          )}
        </div>

        {clearVariant === "button" ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={clearClassName || "text-slate-500 hover:text-red-600"}
            onClick={handleClearSignature}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Cancella firma
          </Button>
        ) : (
          <button
            type="button"
            className={
              clearClassName ||
              "text-sm text-slate-500 flex items-center gap-1.5 hover:text-cyan-700 transition-colors"
            }
            onClick={handleClearSignature}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Cancella firma
          </button>
        )}
      </div>

      {webHidSupported && !tabletActive && (
        <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
          <PenLine className="w-3 h-3 shrink-0" />
          Per le tavolette Wacom STU usa il pulsante dedicato (Chrome o Edge).
        </p>
      )}
    </div>
  );
});

export default SignaturePadField;
